import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import SEO from "../components/SEO";
import { BUSINESS, IMAGES } from "../lib/site";

const INCLUDED = [
  "Engine oil and oil filter change",
  "Fluid level checks and top-ups where required",
  "Brake, steering and suspension checks",
  "Tyre condition and pressure checks",
  "Lights, wipers and visibility checks",
  "General vehicle health inspection",
];

export default function ServicingPage() {
  return (
    <>
      <SEO
        title="Car Servicing in Adlington & Chorley | Interim £100-£150, Full £180-£280"
        description="Car servicing in Adlington, Lancashire. Interim service £100-£150, full service with inspection £180-£280. Honest pricing and professional vehicle maintenance near Chorley. Call 01257 786 540."
        path="/car-servicing-adlington"
      />

      <section className="relative bg-navy overflow-hidden">
        <div className="absolute inset-0 eng-grid" />
        <div className="container-pr7 relative py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="eyebrow-light mb-3">Car Servicing in Adlington</p>
            <h1 className="font-heading font-black text-white text-4xl sm:text-5xl tracking-tight leading-tight">
              Servicing that keeps your car <span className="text-sky">running properly</span>
            </h1>
            <p className="text-pale/75 text-base sm:text-lg leading-relaxed mt-5 max-w-xl">
              Interim and full servicing for all makes and models at our Adlington workshop, with clear guide pricing and no unnecessary work.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-7">
              <Link to="/booking?service=Full Service" data-testid="servicing-book-btn" className="btn-primary">BOOK A SERVICE <ArrowRight size={17} /></Link>
              <a href={BUSINESS.phoneHref} data-testid="servicing-call-btn" className="btn-outline-light">CALL {BUSINESS.phone}</a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <img src={IMAGES.engine} alt="Technician carrying out a car service in Adlington" className="rounded-lg object-cover w-full h-72 lg:h-96 shadow-2xl" loading="eager" />
          </div>
        </div>
      </section>

      <section className="section-pad bg-white">
        <div className="container-pr7">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div data-testid="interim-service-card" className="bg-surface border border-border rounded-lg p-8 hover:shadow-lg hover:-translate-y-1 transition-all duration-200">
              <p className="font-mono font-semibold text-brand text-2xl">£100 - £150</p>
              <h2 className="font-heading font-extrabold text-ink text-2xl mt-2 tracking-tight">Interim Service</h2>
              <p className="text-muted-foreground text-sm leading-relaxed mt-4">
                An interim service covers an <strong className="text-ink">oil and oil filter change</strong> together with essential checks - ideal as a mid-interval service for higher-mileage drivers.
              </p>
              <p className="text-xs text-muted-foreground mt-4">Final pricing varies according to vehicle. Please contact us for confirmation.</p>
              <Link to="/booking?service=Interim Service" data-testid="interim-book-btn" className="mt-6 inline-flex items-center gap-2 font-heading font-bold text-sm text-brand hover:text-sky tracking-wide transition-colors">
                BOOK SERVICE <ArrowRight size={15} />
              </Link>
            </div>
            <div data-testid="full-service-card" className="relative bg-navy rounded-lg p-8 overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-200">
              <div className="absolute inset-0 eng-grid" />
              <div className="relative">
                <p className="font-mono font-semibold text-sky text-2xl">£180 - £280</p>
                <h2 className="font-heading font-extrabold text-white text-2xl mt-2 tracking-tight">Full Service</h2>
                <p className="text-pale/70 text-sm leading-relaxed mt-4">
                  Our full service includes a <strong className="text-white">full inspection</strong> of the vehicle alongside its scheduled service work - the comprehensive annual option.
                </p>
                <p className="text-xs text-pale/50 mt-4">Final pricing varies according to vehicle. Please contact us for confirmation.</p>
                <Link to="/booking?service=Full Service" data-testid="full-book-btn" className="mt-6 inline-flex items-center gap-2 font-heading font-bold text-sm text-sky hover:text-white tracking-wide transition-colors">
                  BOOK FULL SERVICE <ArrowRight size={15} />
                </Link>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mt-14">
            <div>
              <h2 className="font-heading font-extrabold text-ink text-2xl sm:text-3xl tracking-tight">What a service includes</h2>
              <ul className="mt-6 space-y-3">
                {INCLUDED.map((i) => (
                  <li key={i} className="flex gap-3 text-sm text-muted-foreground leading-relaxed">
                    <CheckCircle2 size={17} className="text-brand shrink-0 mt-0.5" /> {i}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="font-heading font-extrabold text-ink text-2xl sm:text-3xl tracking-tight">Service intervals</h2>
              <p className="text-muted-foreground leading-relaxed mt-4 text-sm sm:text-base">
                Most manufacturers recommend a service every 12 months or around 12,000 miles, whichever comes first. Higher-mileage vehicles often benefit from an interim oil and filter change between full services. Check your vehicle handbook for its specific schedule.
              </p>
              <p className="text-muted-foreground leading-relaxed mt-4 text-sm sm:text-base">
                Regular servicing helps maintain reliability, supports fuel economy and protects the long-term value of your vehicle. If you are unsure what your car needs, call us and we will advise.
              </p>
              <div className="bg-pale border border-brand/20 rounded-lg p-6 mt-6">
                <p className="font-heading font-bold text-ink text-sm tracking-wide">PRICING</p>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                  Service prices are guide prices. Final pricing varies depending on your vehicle, its specification, the oil grade required and any additional parts. We always confirm the price before work begins.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
